'use client';
import { useEffect } from 'react';
import {
  useDispatch,
  useFileCollectionFromFileId,
  useFileCollectionsFromParentId,
  useFilesFromFileCollectionId,
  useGeneralCollectionFromProjectId,
  useProject,
} from '@witmetrics/data-store';
import { FILE_COLLECTION_TYPE_ID } from '@witmetrics/utils/constants';
import { COLLECTION_VIEWS } from '@witmetrics/unison-core';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useId } from '@/hooks/useId';
import { useString } from '@/hooks/useString';
import { useArray } from '@/hooks/useArray';
import { fetchCollectionContents } from '@/utils/files';
import { fetchProjectFileData } from '@/api/utils/projects';
import { useCollectionViewSettings } from '@/hooks/useCollectionViewSettings';
import FileCollection from '@/components/FileCollection';
import FileCollectionHeader from '@/components/FileCollectionHeader';
import Section from '@/overlays/CaseOverlay/Section';
import FileCollectionCrumbs, {
  type FileCollectionPath,
} from '@/components/FileCollectionCrumbs';
import FileOverlay from '@/overlays/FileOverlay';
import FileCollectionOverlay from '@/overlays/FileCollectionOverlay';
import { classes, createFilesKeypoint, filterCollectionItems } from './utils';

const { DOCUMENTS } = FILE_COLLECTION_TYPE_ID;

export type FilesSectionProps = {
  isFetchingData: boolean;
};

export default function FilesSection({ isFetchingData }: FilesSectionProps) {
  const dispatch = useDispatch();
  const { onApiError, activeProjectId } = useAppState();
  const generalCollection = useGeneralCollectionFromProjectId(activeProjectId);
  const project = useProject(activeProjectId);
  const [activeCollectionId, setActiveCollectionId] = useId();
  const [baseCollectionId, setBaseCollectionId] = useId();
  const [activeFileId, setActiveFileId, { clear: clearActiveFileId }] = useId();
  const [
    overlayCollectionId,
    setOverlayCollectionId,
    { clear: clearOverlayCollectionId },
  ] = useId();
  const [search, setSearch] = useString();
  const [collectionView, setCollectionView] = useCollectionViewSettings();
  const fileCollections = useFileCollectionsFromParentId(activeCollectionId);
  const files = useFilesFromFileCollectionId(activeCollectionId);
  const activeFileCollection = useFileCollectionFromFileId(activeFileId);
  const [crumbs, setCrumbs, { clear: clearCrumbs }] =
    useArray<FileCollectionPath>();
  const [isFetchingFiles, toggleFetchingFiles] = useToggle(true);
  const [isOverlayOpen, toggleOverlay] = useToggle();

  useEffect(() => {
    if (generalCollection && !baseCollectionId) {
      setBaseCollectionId(generalCollection.id);
      setActiveCollectionId(generalCollection.id);
    }
  }, [generalCollection, baseCollectionId, activeCollectionId]);

  useEffect(() => {
    fetchData();
  }, [activeCollectionId]);

  const handleFetchProjectFileCollection = async () => {
    try {
      if (!activeProjectId) return console.warn('Missing activeProjectId');
      // TODO: pagination -- file & collection ID arrays are all based on Redux state
      toggleFetchingFiles(true);
      const { projectFileCollections, fileCollectionFiles } =
        await fetchProjectFileData(activeProjectId);
      dispatch.addProjectFileCollections(projectFileCollections);
      dispatch.addFileCollectionFiles(fileCollectionFiles);
      // TODO: Make sure its the GENERAL collection
      const generalCollection = projectFileCollections.find(
        (pfc) => pfc.fileCollection.fileCollectionTypeId !== DOCUMENTS
      );
      setActiveCollectionId(generalCollection?.fileCollectionId);
      setBaseCollectionId(generalCollection?.fileCollectionId);
      clearCrumbs();
    } catch (err) {
      toggleFetchingFiles(false);
      onApiError(err, 'Error fetching file collection', () => fetchData());
    }
  };

  const handleFetchCollectionContents = async () => {
    try {
      // TODO: pagination
      if (!activeCollectionId) throw Error('Missing activeCollectionId');
      toggleFetchingFiles(true);
      const data = await fetchCollectionContents(activeCollectionId!);
      dispatch.addFileCollections(data.fileCollections.results);
      dispatch.addFileCollectionFiles(data.files.results);
      setCrumbs(data.path);
      toggleFetchingFiles(false);
    } catch (err) {
      toggleFetchingFiles(false);
      onApiError(err, 'Error fetching file collection contents', () =>
        fetchData()
      );
    }
  };

  const fetchData = () => {
    return activeCollectionId
      ? handleFetchCollectionContents()
      : handleFetchProjectFileCollection();
  };

  const handleOpenFile = (fileId: number) => {
    setActiveFileId(fileId);
    toggleOverlay(true);
  };

  const handleViewFileCollection = (collectionId: number) => {
    setOverlayCollectionId(collectionId);
    toggleOverlay(true);
  };

  const handleImportSequence = async (fileId: number) => {
    handleOpenFile(fileId);
    handleUploadFiles([fileId]);
  };

  const handleUploadFiles = async (fileIds: number[]) => {
    const projectKeypoint = await createFilesKeypoint(project!, fileIds);
    dispatch.addProjectKeypoints([projectKeypoint]);
  };

  const handleCloseOverlay = () => {
    toggleOverlay(false);
    clearActiveFileId();
    clearOverlayCollectionId();
  };

  if (!activeProjectId) return null; // TODO

  const collectionOverlayId =
    activeFileCollection?.id ?? overlayCollectionId ?? activeCollectionId;
  const showFileOverlay =
    activeFileId && !activeFileCollection && isOverlayOpen;
  const showCollectionOverlay =
    !showFileOverlay && collectionOverlayId && isOverlayOpen;

  return (
    <>
      {showFileOverlay && (
        <FileOverlay fileId={activeFileId!} onClose={handleCloseOverlay} />
      )}
      {showCollectionOverlay && (
        <FileCollectionOverlay
          fileCollectionId={collectionOverlayId!}
          initialFileId={activeFileId}
          onClose={handleCloseOverlay}
        />
      )}
      <Section>
        <FileCollectionHeader
          downloadable
          isFetchingData={isFetchingData || isFetchingFiles}
          fileCollectionId={activeCollectionId}
          variant="aurora"
          collectionView={collectionView!}
          search={search}
          onChangeView={setCollectionView}
          onImportSequence={handleImportSequence}
          onUploadFiles={handleUploadFiles}
          onNewFileCollection={setActiveCollectionId}
          onViewFileCollection={handleViewFileCollection}
          onSearch={setSearch}
        />
        {collectionView === COLLECTION_VIEWS.GRID && (
          <FileCollectionCrumbs
            className={classes.breadcrumbs}
            homeValue={baseCollectionId}
            crumbs={crumbs}
            onSelect={(collectionId) =>
              setActiveCollectionId(collectionId || baseCollectionId)
            }
          />
        )}
        <FileCollection
          isFetchingData={isFetchingData || isFetchingFiles}
          className={classes.fileCollection}
          parentFileCollectionId={activeCollectionId}
          variant="aurora"
          collectionView={collectionView!}
          files={filterCollectionItems(files, search)}
          fileCollections={filterCollectionItems(fileCollections, search)}
          onViewFile={handleOpenFile}
          onViewFileCollection={handleViewFileCollection}
          onOpenFileCollection={setActiveCollectionId}
        />
      </Section>
    </>
  );
}
